<template>
  <div class="message">
    <div
      v-if="comment?.text"
      ref="text"
      class="description"
      :class="{ slice: isCollapsed }"
    >
      {{ comment.text }}
      <div
        v-if="isCollapsed"
        class="more"
        @click="expandText"
      >
        Ещё
      </div>
    </div>
    <GuideMessageInfo
      :comment="comment"
      @guide-click="(id) => emit('guideClick', id)"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, useTemplateRef } from 'vue';

  import type { SightGuideComment } from '@/shared/types/sight-modal';
  import GuideMessageInfo from '@/shared/ui/sights/GuideMessageInfo.vue';

  const textRef = useTemplateRef('text');

  defineProps<{
    comment: SightGuideComment;
  }>();

  const emit = defineEmits<{
    guideClick: [guideId: number];
  }>();

  const TEXT_MAX_HEIGHT = 96;

  const isExpanded = ref(false);

  const isOverflow = computed(() => (textRef.value?.offsetHeight ?? 0) > TEXT_MAX_HEIGHT);

  const isCollapsed = computed(() => isOverflow.value && !isExpanded.value);

  const expandText = () => {
    isExpanded.value = true;
  };
</script>

<style lang="scss" scoped>
  .message {
    gap: var(--alias-space-16);
    padding: var(--alias-space-12);
    background: var(--alias-color-background-subtle);
    border-radius: var(--alias-radius-16, 16px);
  }

  .description {
    &:hover {
      .more {
        color: var(--alias-color-link-default-hovered);
        text-decoration-color: var(--alias-color-link-default-hovered);
      }
    }

    @include base-400-400-regular;
  }

  .more {
    position: absolute;
    right: 0;
    bottom: 0;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-color: var(--alias-color-text-subtler);
    text-decoration-skip-ink: none;
    cursor: pointer;
    background-color: var(--alias-color-background-subtle);

    &::before {
      position: absolute;
      top: 0;
      left: -8px;
      width: 8px;
      height: 100%;
      content: '';
      background-color: var(--alias-color-background-subtle);
    }

    @include base-400-400-regular;
  }

  .slice {
    position: relative;
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
  }
</style>
